import { FC, useEffect, useState } from 'react'

import cn from 'classnames'

import styles from './styles.module.scss'

interface PaginationProps {
  pagesCount: number
  activePage: number
  onPageChange: (page: number) => void
}

export const Pagination: FC<PaginationProps> = ({ pagesCount, activePage, onPageChange }) => {
  const [firstNumbers, setFirstNumbers] = useState<number[]>([])
  const [lastNumbers, setLastNumbers] = useState<number[]>([])
  const maxVisiblePages = 6

  const goToPage = (page: number) => {
    onPageChange(page)
  }

  const prevPage = () => {
    if (activePage > 1) {
      goToPage(activePage - 1)
    }
  }

  const nextPage = () => {
    if (activePage < pagesCount) {
      goToPage(activePage + 1)
    }
  }

  useEffect(() => {
    if (pagesCount === 0) return

    if (pagesCount > maxVisiblePages) {
      setFirstNumbers([1, 2, 3])
      setLastNumbers([pagesCount - 2, pagesCount - 1, pagesCount])
    } else {
      setFirstNumbers(Array.from({ length: pagesCount }, (_, index) => index + 1))
      setLastNumbers([])
    }
  }, [pagesCount])

  useEffect(() => {
    if (pagesCount === 0) return

    if (pagesCount > maxVisiblePages) {
      setFirstNumbers((prevFirstNumbers) => {
        let newFirstNumbers = prevFirstNumbers.length === 0 ? [1, 2, 3] : prevFirstNumbers
        if (
          activePage === prevFirstNumbers[prevFirstNumbers.length - 1] &&
          activePage < pagesCount - 2 &&
          activePage === lastNumbers[0] - 1
        ) {
          return prevFirstNumbers
        } else if (
          activePage === prevFirstNumbers[prevFirstNumbers.length - 1] + 1 &&
          activePage < pagesCount - 2 &&
          activePage === lastNumbers[0]
        ) {
          return prevFirstNumbers
        } else if (activePage === prevFirstNumbers[prevFirstNumbers.length - 1] && activePage < pagesCount - 2) {
          newFirstNumbers = [prevFirstNumbers[1], prevFirstNumbers[2], prevFirstNumbers[2] + 1]
        } else if (activePage === prevFirstNumbers[0] && prevFirstNumbers[0] > 1) {
          newFirstNumbers = [prevFirstNumbers[0] - 1, prevFirstNumbers[0], prevFirstNumbers[1]]
        }
        if (newFirstNumbers[newFirstNumbers.length - 1] >= pagesCount - 2) {
          newFirstNumbers = newFirstNumbers.filter((num) => num < pagesCount - 2)
        }
        return newFirstNumbers
      })

      setLastNumbers((prevLastNumbers) => {
        let newLastNumbers =
          prevLastNumbers.length === 0 ? [pagesCount - 2, pagesCount - 1, pagesCount] : prevLastNumbers
        if (
          activePage === prevLastNumbers[0] &&
          prevLastNumbers[0] > 1 &&
          activePage === firstNumbers[firstNumbers.length - 1]
        ) {
          return prevLastNumbers
        } else if (
          activePage === prevLastNumbers[0] &&
          prevLastNumbers[0] > 1 &&
          activePage === firstNumbers[firstNumbers.length - 1] + 1
        ) {
          return prevLastNumbers
        } else if (activePage === prevLastNumbers[0] && prevLastNumbers[0] > 1) {
          newLastNumbers = [prevLastNumbers[0] - 1, prevLastNumbers[0], prevLastNumbers[1]]
        } else if (activePage === prevLastNumbers[prevLastNumbers.length - 1] && activePage < pagesCount) {
          newLastNumbers = [prevLastNumbers[1], prevLastNumbers[2], prevLastNumbers[2] + 1]
        }
        if (newLastNumbers[0] <= 3) {
          newLastNumbers = newLastNumbers.filter((num) => num > 3)
        }
        return newLastNumbers
      })
    } else {
      setFirstNumbers(Array.from({ length: pagesCount }, (_, index) => index + 1))
      setLastNumbers([])
    }
  }, [pagesCount, activePage])

  return (
    <div className={styles.pagination}>
      <div className={cn(styles.arrow, styles.left, { [styles.active]: activePage > 1 })} onClick={prevPage}></div>
      {pagesCount > maxVisiblePages ? (
        <>
          {firstNumbers[0] !== 1 ? <div className={styles.ellipsis}>...</div> : ''}
          {firstNumbers.map((page) => (
            <span onClick={() => goToPage(page)} className={cn({ [styles.active]: page === activePage })} key={page}>
              {page}
            </span>
          ))}
          {firstNumbers[firstNumbers.length - 1] + 1 !== lastNumbers[0] ? (
            <div className={styles.ellipsis}>...</div>
          ) : (
            ''
          )}
          {lastNumbers.map((page) => (
            <span onClick={() => goToPage(page)} className={cn({ [styles.active]: page === activePage })} key={page}>
              {page}
            </span>
          ))}
          {lastNumbers[lastNumbers.length - 1] !== pagesCount ? <div className={styles.ellipsis}>...</div> : ''}
        </>
      ) : (
        <>
          {Array.from(Array(pagesCount), (_, index) => index + 1).map((page) => (
            <span onClick={() => goToPage(page)} className={cn({ [styles.active]: page === activePage })} key={page}>
              {page}
            </span>
          ))}
        </>
      )}
      <div
        className={cn(styles.arrow, styles.right, { [styles.active]: activePage < pagesCount })}
        onClick={nextPage}
      ></div>
    </div>
  )
}
